// ═══════════════════════════════════════════════════════
// ULTIMAZE TOWER WARS — PushPermissionModal
// Soft-ask modal shown BEFORE the OS permission dialog.
// Apple/Android best practice: explain value before prompting.
// ═══════════════════════════════════════════════════════

import React from 'react'
import { useTranslation } from 'react-i18next'
import { NotificationService } from '../services/NotificationService'
import { S } from '../locales/strings.js'

interface PushPermissionModalProps {
  onAccept: () => void
  onDecline: () => void
}

export function PushPermissionModal({ onAccept, onDecline }: PushPermissionModalProps): React.ReactElement {
  const { t } = useTranslation()
  const [isRequesting, setIsRequesting] = React.useState(false)

  const handleAccept = async () => {
    setIsRequesting(true)
    try {
      await NotificationService.getInstance().requestPermissions()
    } catch {
      // Permission request failed or adapter not ready — proceed anyway
    } finally {
      setIsRequesting(false)
      onAccept()
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        padding: '24px',
      }}
    >
      <div
        style={{
          backgroundColor: '#1C1526',
          borderRadius: '16px',
          padding: '32px 24px',
          maxWidth: '360px',
          width: '100%',
          border: '1px solid #3b2d52',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          textAlign: 'center',
        }}
      >
        {/* Bell icon */}
        <div
          style={{
            fontSize: '64px',
            lineHeight: 1,
            marginBottom: '16px',
          }}
        >
          🔔
        </div>

        {/* Title */}
        <h2
          style={{
            color: '#f5c542',
            fontSize: '22px',
            fontWeight: 700,
            margin: '0 0 12px',
            fontFamily: 'inherit',
          }}
        >
          {t(S.PUSH_MODAL_TITLE)}
        </h2>

        {/* Body */}
        <p
          style={{
            color: '#c4b5d4',
            fontSize: '14px',
            lineHeight: 1.5,
            margin: '0 0 20px',
          }}
        >
          {t(S.PUSH_MODAL_BODY)}
        </p>

        {/* Bullet points */}
        <div
          style={{
            backgroundColor: '#251a38',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '24px',
            textAlign: 'left',
          }}
        >
          <p style={{ color: '#d4c8e8', fontSize: '14px', margin: '0 0 8px', lineHeight: 1.4 }}>
            {t(S.PUSH_MODAL_BULLET_PACK)}
          </p>
          <p style={{ color: '#d4c8e8', fontSize: '14px', margin: 0, lineHeight: 1.4 }}>
            {t(S.PUSH_MODAL_BULLET_PVP)}
          </p>
        </div>

        {/* Accept button */}
        <button
          onClick={handleAccept}
          disabled={isRequesting}
          style={{
            display: 'block',
            width: '100%',
            padding: '14px',
            marginBottom: '10px',
            backgroundColor: isRequesting ? '#6b3fa0' : '#a855f7',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: 700,
            cursor: isRequesting ? 'not-allowed' : 'pointer',
            minHeight: '44px',
            transition: 'background-color 0.15s',
          }}
        >
          {isRequesting ? t(S.PUSH_MODAL_SETTING_UP) : t(S.PUSH_MODAL_ACCEPT)}
        </button>

        {/* Decline button */}
        <button
          onClick={onDecline}
          disabled={isRequesting}
          style={{
            display: 'block',
            width: '100%',
            padding: '12px',
            backgroundColor: 'transparent',
            color: '#9081a8',
            border: '1px solid #3b2d52',
            borderRadius: '10px',
            fontSize: '14px',
            cursor: isRequesting ? 'not-allowed' : 'pointer',
            minHeight: '44px',
          }}
        >
          {t(S.PUSH_MODAL_DECLINE)}
        </button>
      </div>
    </div>
  )
}
